import { BaseChart } from './BaseChart.js';

export class LineChart extends BaseChart {
    constructor({
        // DATOS
        /**
         * Array de objetos que representan los puntos de la línea.
         * Cada objeto debe contener las claves definidas en `xKey` y `yKey`.
         * Ejemplo:
         * [
         *   { name: 'Enero', value: 120 },
         *   { name: 'Febrero', value: 150 },
         *   { name: 'Marzo', value: 170 }
         * ]
         */
        data = [],
        xKey = 'name',              // Clave del eje X (categorías o tiempo)
        yKey = 'value',             // Clave del eje Y (valores numéricos)

        // ESTILOS DE LÍNEA
        lineColor = '#3b82f6',      // Color de la línea principal
        lineWidth = 2,              // Grosor de la línea en píxeles
        lineType = 'solid',         // 'solid', 'dashed', 'dotted'

        // SÍMBOLOS (PUNTOS)
        showSymbols = true,         // Mostrar puntos/marcadores en cada dato
        symbolSize = 8,             // Tamaño de los símbolos

        // SUAVIZADO Y ÁREA
        smooth = false,             // true = línea curva, false = recta entre puntos
        areaStyle = false,          // true = rellenar área bajo la línea
        areaGradient = ['rgba(59,130,246,0.4)', 'rgba(59,130,246,0.1)'],    // Gradiente área

        // LABELS
        showLabels = false,         // Mostrar valores numéricos
        labelPosition = 'top',      // 'top', 'inside', 'bottom', 'left', 'right'
        labelFontSize = 12,         // Tamaño de fuente
        labelColor = '#000',        // Color del texto
        labelFontWeight = 'normal', // Peso: 'normal', 'bold', etc.
        labelFormatter = null,      // Función: (params) => params.value.toLocaleString()
        axisFontSize = 10,          // Tamaño de letra para ejes X e Y

        // EJES
        xAxisName = '',             // Nombre/título eje X
        yAxisName = '',             // Nombre/título eje Y
        xAxisNameGap = 30,          // Distancia nombre-eje X
        yAxisNameGap = 50,          // Distancia nombre-eje Y
        xAxisRotate = 0,            // Rotación etiquetas eje X
        yAxisRotate = 0,            // Rotación etiquetas eje Y
        showXAxisLine = true,       // Mostrar línea eje X
        showYAxisLine = true,       // Mostrar línea eje Y
        showXAxisSplitLine = false, // Líneas de cuadrícula vertical
        showYAxisSplitLine = true,  // Líneas de cuadrícula horizontal
        xAxisLabelFormatter = null, // Función para formatear etiquetas X
        yAxisLabelFormatter = null, // Función para formatear etiquetas Y

        // INTERACTIVIDAD
        selectedMode = false,       // false, 'single', 'multiple'
        selectedOffset = 5,         // Separación visual al seleccionar

        // MÚLTIPLES SERIES
        /**
         * Configuración para gráficos con múltiples líneas comparativas.
         * Si multipleSeries = true, usar seriesList con formato:
         * [
         *   {
         *     name: 'Serie 1',
         *     data: [{ value: 10 }, { value: 20 }],
         *     color: '#ff0000'
         *   },
         *   {
         *     name: 'Serie 2',
         *     data: [{ value: 15 }, { value: 25 }],
         *     color: '#00ff00'
         *   }
         * ]
         */
        multipleSeries = false,
        seriesList = [],

        // PROPIEDADES BASE
        ...baseProps
    } = {}) {
        // Constructor de la clase base con configuraciones específicas
        super({
            ...baseProps,
            // Sobrescribir defaults específicos para líneas
            tooltipTrigger: baseProps.tooltipTrigger || 'axis',
            tooltipAxisPointer: baseProps.tooltipAxisPointer || 'line',
            toolboxFeatures: baseProps.toolboxFeatures || ['dataZoom', 'magicType', 'restore', 'saveAsImage'],
            gridLeft: baseProps.gridLeft || '5%',
            gridRight: baseProps.gridRight || '5%',
            emphasis: baseProps.emphasis || {
                focus: 'series',
                itemStyle: {
                    shadowBlur: 10,
                    shadowColor: 'rgba(0, 0, 0, 0.4)',
                }
            },
        });
        // Propiedades específicas de LineChart
        this.data = data;
        this.xKey = xKey;
        this.yKey = yKey;
        this.lineColor = lineColor;
        this.lineWidth = lineWidth;
        this.lineType = lineType;
        this.showSymbols = showSymbols;
        this.symbolSize = symbolSize;
        this.smooth = smooth;
        this.areaStyle = areaStyle;
        this.areaGradient = areaGradient;
        this.showLabels = showLabels;
        this.labelPosition = labelPosition;
        this.labelFontSize = labelFontSize;
        this.labelColor = labelColor;
        this.labelFontWeight = labelFontWeight;
        this.labelFormatter = labelFormatter;
        this.axisFontSize = axisFontSize;
        this.xAxisName = xAxisName;
        this.yAxisName = yAxisName;
        this.xAxisNameGap = xAxisNameGap;
        this.yAxisNameGap = yAxisNameGap;
        this.xAxisRotate = xAxisRotate;
        this.yAxisRotate = yAxisRotate;
        this.showXAxisLine = showXAxisLine;
        this.showYAxisLine = showYAxisLine;
        this.showXAxisSplitLine = showXAxisSplitLine;
        this.showYAxisSplitLine = showYAxisSplitLine;
        this.xAxisLabelFormatter = xAxisLabelFormatter;
        this.yAxisLabelFormatter = yAxisLabelFormatter;
        this.selectedMode = selectedMode;
        this.selectedOffset = selectedOffset;
        this.multipleSeries = multipleSeries;
        this.seriesList = seriesList;
    }

    getOptions() {
        // Extrae las etiquetas para el eje X y determina qué nombres deben aparecer en la leyenda 
        const xValues = this.data.map(d => d[this.xKey] ?? 'Sin nombre');
        const legendNames = this.multipleSeries ? this.seriesList.map(s => s.name) : [this.seriesName];

        const baseSeries = {
            id: this.seriesId,
            name: this.seriesName,
            type: 'line',
            symbolSize: this.symbolSize,
            showSymbol: this.showSymbols,
            smooth: this.smooth,
            lineStyle: {
                width: this.lineWidth,
                type: this.lineType,
            },
            label: {
                show: this.showLabels,
                position: this.labelPosition,
                fontSize: this.labelFontSize,
                color: this.labelColor,
                fontWeight: this.labelFontWeight,
                formatter: this.labelFormatter || ((params) => params.value.toLocaleString()),
            },
            emphasis: this.emphasis,
            blur: this.blur,
            selectedMode: this.selectedMode,
            animationDuration: this.animationDuration,
            animationDurationUpdate: this.animationDurationUpdate,
            animationEasing: this.animationEasing,
            animationDelay: this.animationDelay,
        };

        // Construye el array de series para ECharts. Si es multiserie, mapea la lista de series con sus respectivos colores y gradientes. Si es serie única, usa los datos directos.
        const series = this.multipleSeries
            ? this.seriesList.map((s, index) => ({
                ...baseSeries,
                id: `${this.seriesId || 'line-series'}-${index}`,
                name: s.name,
                data: s.data.map(d => d.value ?? 0),
                itemStyle: { color: s.color ?? this.lineColor },
                lineStyle: {
                    ...baseSeries.lineStyle,
                    color: s.color ?? this.lineColor,
                },
                areaStyle: this.areaStyle ? {
                    color: {
                        type: 'linear',
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [
                            { offset: 0, color: (s.color ?? this.lineColor) + 'aa' },
                            { offset: 1, color: 'rgba(255,255,255,0)' },
                        ],
                    }
                } : undefined
            }))
            : [{
                ...baseSeries,
                data: this.data.map(d => d[this.yKey] ?? 0),
                itemStyle: { color: this.lineColor },
                lineStyle: { ...baseSeries.lineStyle, color: this.lineColor },
                areaStyle: this.areaStyle ? {
                    color: {
                        type: 'linear',
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [
                            { offset: 0, color: this.areaGradient[0] },
                            { offset: 1, color: this.areaGradient[1] },
                        ],
                    },
                    opacity: 0.6,
                } : undefined
            }];

        // Integra todos los componentes: Título, Leyenda, Tooltip formateado, Grid (márgenes) y la configuración técnica de los ejes X e Y (nombres, rotación y espaciado).
        const options = {
            title: this.getTitleConfig(),
            legend: {
                ...this.getLegendConfig(),
                data: legendNames
            },
            tooltip: this.getTooltipConfig((params) => {
                if (Array.isArray(params)) {
                    return `<div style="padding:5px">
                                <b style="display:block; margin-bottom:5px; border-bottom:1px solid #ddd">${params[0].name}</b>
                                ${params.map(p => `
                                    <div style="display:flex; justify-content:space-between; gap:15px">
                                        <span>${p.marker} ${p.seriesName}:</span>
                                        <b>${p.value.toLocaleString()}</b>
                                    </div>`).join('')}
                            </div>`;
                }
                return `<div><strong>${params.name}</strong>: ${params.value}</div>`;
            }),
            grid: {
                ...this.getGridConfig(),
                left: this.gridLeft || '10%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                name: this.xAxisName,
                nameLocation: 'middle',
                nameGap: this.xAxisNameGap || 35,
                data: xValues,
                nameTextStyle: { fontSize: this.axisFontSize, fontWeight: 'bold' },
                axisLabel: {
                    rotate: this.xAxisRotate,
                    formatter: this.xAxisLabelFormatter,
                    fontSize: this.axisFontSize
                },
                axisLine: { show: this.showXAxisLine },
                splitLine: { show: this.showXAxisSplitLine },
            },
            yAxis: {
                type: 'value',
                name: this.yAxisName,
                nameLocation: 'middle',
                nameRotate: 90,
                nameGap: this.yAxisNameGap || 45,
                nameTextStyle: {
                    fontSize: this.axisFontSize,
                    fontWeight: 'bold',
                },
                axisLabel: {
                    rotate: this.yAxisRotate,
                    formatter: this.yAxisLabelFormatter,
                    fontSize: this.axisFontSize
                },
                axisLine: { show: this.showYAxisLine },
                splitLine: {
                    show: this.showYAxisSplitLine,
                    lineStyle: { type: 'dashed', opacity: 0.4 }
                },
            },
            series: series,
        };

        // Inyecta la barra de herramientas (Toolbox) y los controles de Zoom (DataZoom)
        const toolbox = this.getToolboxConfig();
        if (toolbox && toolbox.show) options.toolbox = toolbox;

        const dataZoom = this.getDataZoomConfig([0], true);
        if (dataZoom) options.dataZoom = dataZoom;

        return options;
    }
}
